// Port of TrailData/Import/ImportPropertyMapping.swift: maps free-form feature properties (GeoJSON `properties`, KML
// `ExtendedData`, OpenStreetMap tags) to domain values. Property keys are expected lower-cased (importers normalise
// them); values are compared as normalised tokens (case-, underscore-, hyphen- and space-insensitive), so
// `mountainHut`, `mountain_hut` and `Mountain Hut` are equivalent.

import { allPOICategories } from '../core/domain';
import type {
  AccessRule,
  AccommodationAttributes,
  Confidence,
  CountryCode,
  OvernightRule,
  POIAttributes,
  POICategory,
  ProtectedAreaKind,
  SACScale,
  SeasonalWindow,
  WaterAttributes,
} from '../core/models';
import { trimWhitespace, trimWhitespaceAndNewlines } from './text';

/** Feature properties: lower-cased keys → trimmed string values. */
export type ImportProperties = Readonly<Record<string, string>>;

// MARK: Tokens

/** Lower-cased token without `_`, `-` and spaces. */
export function token(raw: string): string {
  return raw.toLowerCase().replace(/[_\- ]/g, '');
}

/** The first non-empty (trimmed) value among `keys`. */
export function value(properties: ImportProperties, ...keys: string[]): string | undefined {
  for (const key of keys) {
    if (!Object.hasOwn(properties, key)) continue;
    const v = trimWhitespaceAndNewlines(properties[key]);
    if (v !== '') return v;
  }
  return undefined;
}

export function isTruthy(raw: string | undefined): boolean {
  if (raw === undefined) return false;
  return ['yes', 'true', '1'].includes(raw.toLowerCase());
}

// MARK: POI category

/** Explicit category tokens accepted in `trailbivvy:category` / `category`, besides `POICategory` raw values. */
const categoryAliases: Record<string, POICategory> = {
  hut: 'mountainHut',
  alpinehut: 'mountainHut',
  cabane: 'mountainHut',
  rifugio: 'mountainHut',
  bivouac: 'bivouacHut',
  bivouachut: 'bivouacHut',
  bivacco: 'bivouacHut',
  wildernesshut: 'shelter',
  campsite: 'camping',
  campground: 'camping',
  hotel: 'hotelHostel',
  hostel: 'hotelHostel',
  guesthouse: 'hotelHostel',
  drinkingwater: 'water',
  spring: 'water',
  fountain: 'water',
  supermarket: 'foodShop',
  grocery: 'foodShop',
  convenience: 'foodShop',
  station: 'train',
  railway: 'train',
  trainstation: 'train',
  busstop: 'bus',
  cablecar: 'cableCar',
  gondola: 'cableCar',
  aerialway: 'cableCar',
  hamlet: 'village',
  town: 'village',
};

const categoryByToken: ReadonlyMap<string, POICategory> = (() => {
  const map = new Map<string, POICategory>(Object.entries(categoryAliases));
  // `parking` is not an import category: car parks are bundled OSM data with typed attributes and are not drawn on the
  // map, so an imported "parking" point stays a visible waypoint (iOS DECISION.md PK-9).
  for (const category of allPOICategories) if (category !== 'parking') map.set(token(category), category);
  return map;
})();

/** Category from an explicit token (`POICategory` raw value or a documented alias). */
export function categoryFromToken(raw: string): POICategory | undefined {
  return categoryByToken.get(token(raw));
}

/**
 * POI category from explicit properties (`trailbivvy:category`, then `category`), else from OSM tags. Among OSM tags,
 * `tourism` overnight tags win (a hotel with a restaurant is an overnight place).
 */
export function poiCategory(p: ImportProperties): POICategory | undefined {
  for (const key of ['trailbivvy:category', 'category']) {
    const raw = value(p, key);
    const category = raw === undefined ? undefined : categoryFromToken(raw);
    if (category) return category;
  }
  const tourism = value(p, 'tourism')?.toLowerCase();
  const amenity = value(p, 'amenity')?.toLowerCase();
  switch (tourism) {
    case 'alpine_hut':
      return 'mountainHut';
    case 'wilderness_hut':
      return 'shelter';
    case 'camp_site':
      return 'camping';
    case 'hotel':
    case 'hostel':
    case 'guest_house':
      return 'hotelHostel';
  }
  if (amenity === 'drinking_water' || value(p, 'natural')?.toLowerCase() === 'spring') return 'water';
  if (amenity === 'shelter') {
    // A bus-stop shelter is not a place to spend the night.
    if (value(p, 'shelter_type')?.toLowerCase() !== 'public_transport') return 'shelter';
  }
  const shop = value(p, 'shop')?.toLowerCase();
  if (shop === 'supermarket' || shop === 'convenience') return 'foodShop';
  switch (amenity) {
    case 'restaurant':
      return 'restaurant';
    case 'pharmacy':
      return 'pharmacy';
    case 'doctors':
      return 'doctor';
  }
  const railway = value(p, 'railway')?.toLowerCase();
  if (railway === 'station' || railway === 'halt') return 'train';
  if (value(p, 'highway')?.toLowerCase() === 'bus_stop') return 'bus';
  if (value(p, 'aerialway')?.toLowerCase() === 'station') return 'cableCar';
  const place = value(p, 'place')?.toLowerCase();
  if (place !== undefined && ['village', 'hamlet', 'town'].includes(place)) return 'village';
  return undefined;
}

/** Swift `Int(String)`: optional sign and ASCII digits only. */
function parseInt(raw: string): number | undefined {
  if (!/^[+-]?\d+$/.test(raw)) return undefined;
  const n = Number(raw);
  return Number.isSafeInteger(n) ? n : undefined;
}

/** `http(s)` URL (Swift `URL(string:)` + scheme check). */
function webURL(raw: string): string | undefined {
  const scheme = /^([A-Za-z][A-Za-z0-9+.-]*):/.exec(raw)?.[1].toLowerCase();
  if (scheme !== 'https' && scheme !== 'http') return undefined;
  try {
    new URL(raw);
  } catch {
    return undefined;
  }
  return raw;
}

/** Typed attributes derivable from common OSM tags. */
export function poiAttributes(category: POICategory, p: ImportProperties): POIAttributes {
  const attributes: POIAttributes = {};
  const website = value(p, 'website', 'contact:website', 'url');
  const url = website === undefined ? undefined : webURL(website);
  if (url !== undefined) attributes.website = url;
  const phone = value(p, 'phone', 'contact:phone');
  if (phone !== undefined) attributes.phone = phone;
  const operatorName = value(p, 'operator');
  if (operatorName !== undefined) attributes.operatorName = operatorName;
  const capacityText = value(p, 'capacity', 'beds');
  const capacity = capacityText === undefined ? undefined : parseInt(capacityText);
  if (capacity !== undefined) attributes.capacity = capacity;
  const note = value(p, 'description', 'note');
  if (note !== undefined) attributes.note = Array.from(note).slice(0, 500).join('');
  if (isTruthy(value(p, 'seasonal'))) attributes.isYearRound = false;

  const tourism = value(p, 'tourism')?.toLowerCase();
  const stopIdentifier = value(p, 'ref:sloid', 'uic_ref');
  const accommodation = (kind: AccommodationAttributes['kind'], allowsTents?: boolean): AccommodationAttributes =>
    allowsTents === undefined ? { kind } : { kind, allowsTents };
  switch (category) {
    case 'mountainHut':
      attributes.accommodation = accommodation('mountainHut');
      break;
    case 'bivouacHut':
      attributes.accommodation = accommodation('bivouacHut');
      break;
    case 'shelter':
      attributes.accommodation = accommodation(tourism === 'wilderness_hut' ? 'bivouacHut' : 'shelter');
      break;
    case 'camping': {
      const tents = value(p, 'tents')?.toLowerCase();
      attributes.accommodation = accommodation('campsite', tents !== 'no');
      break;
    }
    case 'hotelHostel':
      if (tourism === 'hotel') attributes.accommodation = accommodation('hotel');
      else if (tourism === 'hostel') attributes.accommodation = accommodation('hostel');
      else if (tourism === 'guest_house') attributes.accommodation = accommodation('guesthouse');
      break;
    case 'water': {
      const drinking = value(p, 'drinking_water')?.toLowerCase();
      let potability: WaterAttributes['potability'];
      if (drinking === 'no') potability = 'treatBeforeDrinking';
      else if (drinking === 'yes' || value(p, 'amenity')?.toLowerCase() === 'drinking_water') potability = 'potable';
      else potability = 'unknown';
      let reliability: WaterAttributes['reliability'];
      if (isTruthy(value(p, 'intermittent'))) reliability = 'unreliable';
      else if (isTruthy(value(p, 'seasonal'))) reliability = 'seasonal';
      else reliability = 'unknown';
      attributes.water = { potability, reliability };
      break;
    }
    case 'foodShop':
      attributes.food = { kind: 'grocery' };
      break;
    case 'restaurant':
      attributes.food = { kind: 'restaurant' };
      break;
    case 'train':
    case 'bus':
    case 'cableCar':
      attributes.transit = stopIdentifier === undefined ? { mode: category } : { mode: category, stopIdentifier };
      break;
    default:
      break;
  }
  return attributes;
}

// MARK: Protected areas

/** Whether properties explicitly describe a protected area (as opposed to e.g. a campsite mapped as an area). */
export function indicatesProtectedArea(p: ImportProperties): boolean {
  if (
    value(p, 'trailbivvy:kind', 'kind', 'overnight_rule', 'overnightrule', 'trailbivvy:overnight_rule', 'protect_class') !==
    undefined
  ) {
    return true;
  }
  const boundary = value(p, 'boundary')?.toLowerCase();
  if (boundary === 'protected_area' || boundary === 'national_park') return true;
  return value(p, 'leisure')?.toLowerCase() === 'nature_reserve';
}

const protectedAreaKinds: readonly ProtectedAreaKind[] = [
  'nationalPark',
  'natureReserve',
  'wildlifeRestZone',
  'wildlifeReserve',
  'moorland',
  'floodplain',
  'dryGrassland',
  'landscapeInventory',
  'unescoSite',
  'other',
];

const kindAliases: Record<string, ProtectedAreaKind> = {
  wildruhezone: 'wildlifeRestZone',
  wildliferefuge: 'wildlifeRestZone',
  jagdbanngebiet: 'wildlifeReserve',
  nationalpark: 'nationalPark',
  naturereserve: 'natureReserve',
  naturschutzgebiet: 'natureReserve',
  bln: 'landscapeInventory',
  unesco: 'unescoSite',
};

/** Kind from `kind` / `trailbivvy:kind`, else from OSM tags, else `other`. */
export function protectedAreaKind(p: ImportProperties): ProtectedAreaKind {
  const raw = value(p, 'trailbivvy:kind', 'kind');
  if (raw !== undefined) {
    const t = token(raw);
    const kind = protectedAreaKinds.find((k) => token(k) === t) ?? (Object.hasOwn(kindAliases, t) ? kindAliases[t] : undefined);
    if (kind) return kind;
  }
  if (value(p, 'boundary')?.toLowerCase() === 'national_park') return 'nationalPark';
  if (value(p, 'leisure')?.toLowerCase() === 'nature_reserve') return 'natureReserve';
  switch (value(p, 'protect_class')?.toLowerCase()) {
    case '2':
      return 'nationalPark';
    case '1':
    case '1a':
    case '1b':
    case '4':
      return 'natureReserve';
    default:
      return 'other';
  }
}

const overnightRules: readonly OvernightRule[] = ['prohibited', 'restricted', 'allowedAtDesignatedSites', 'noRuleKnown', 'unknown'];

const overnightAliases: Record<string, OvernightRule> = {
  forbidden: 'prohibited',
  banned: 'prohibited',
  designated: 'allowedAtDesignatedSites',
  designatedsites: 'allowedAtDesignatedSites',
  designatedsitesonly: 'allowedAtDesignatedSites',
};

/** Overnight rule from `overnight_rule`; `unknown` when absent or unrecognised (never permissive). */
export function overnightRule(p: ImportProperties): { rule: OvernightRule; unrecognised?: string } {
  const raw = value(p, 'trailbivvy:overnight_rule', 'overnight_rule', 'overnightrule');
  if (raw === undefined) return { rule: 'unknown' };
  const t = token(raw);
  const rule = overnightRules.find((r) => token(r) === t) ?? (Object.hasOwn(overnightAliases, t) ? overnightAliases[t] : undefined);
  return rule ? { rule } : { rule: 'unknown', unrecognised: raw };
}

const accessRules: readonly AccessRule[] = ['none', 'stayOnMarkedTrails', 'noAccess', 'unknown'];

export function accessRule(p: ImportProperties): AccessRule {
  const raw = value(p, 'access_rule', 'accessrule');
  if (raw === undefined) return 'unknown';
  if ((accessRules as readonly string[]).includes(raw)) return raw as AccessRule;
  return accessRules.find((r) => token(r) === token(raw)) ?? 'unknown';
}

function monthDay(raw: string): { month: number; day: number } | undefined {
  const parts = raw
    .split('-')
    .filter((s) => s !== '')
    .map((s) => parseInt(s))
    .filter((n): n is number => n !== undefined);
  if (parts.length !== 2 || parts[0] < 1 || parts[0] > 12 || parts[1] < 1 || parts[1] > 31) return undefined;
  return { month: parts[0], day: parts[1] };
}

/** `season_start` / `season_end` as `MM-DD` (also `--MM-DD`), e.g. a wildlife rest zone active 12-20 → 04-30. */
export function seasonalWindow(p: ImportProperties): SeasonalWindow | undefined {
  const startText = value(p, 'season_start');
  const endText = value(p, 'season_end');
  const start = startText === undefined ? undefined : monthDay(startText);
  const end = endText === undefined ? undefined : monthDay(endText);
  if (!start || !end) return undefined;
  return { startMonth: start.month, startDay: start.day, endMonth: end.month, endDay: end.day };
}

export function regulationIDs(p: ImportProperties): string[] {
  const raw = value(p, 'regulation_ids', 'regulationids');
  if (raw === undefined) return [];
  return raw
    .split(/[,;]/)
    .map((s) => trimWhitespace(s))
    .filter((s) => s !== '');
}

// MARK: Common

const confidences: readonly Confidence[] = ['low', 'medium', 'high'];

export function confidence(p: ImportProperties): Confidence | undefined {
  const raw = value(p, 'confidence')?.toLowerCase();
  return raw !== undefined && (confidences as readonly string[]).includes(raw) ? (raw as Confidence) : undefined;
}

/** ISO 3166-1 alpha-2 country code from `country`, `country_code`, `countrycode` or `addr:country`. */
export function countryCode(p: ImportProperties): CountryCode | undefined {
  const raw = value(p, 'country', 'country_code', 'countrycode', 'addr:country');
  if (raw === undefined || !/^[A-Za-z]{2}$/.test(raw)) return undefined;
  return raw.toUpperCase();
}

export function name(p: ImportProperties): string | undefined {
  return value(p, 'name', 'title', 'name:en', 'name:de', 'name:fr', 'name:it');
}

/** Swift `Double(String)` for decimal text (hexadecimal floats aside; "nan"/"inf" are accepted, then rejected as non-finite). */
export function parseDouble(raw: string): number | undefined {
  if (/^[+-]?(?:\d+\.?\d*|\.\d+)(?:[eE][+-]?\d+)?$/.test(raw)) return Number(raw);
  const special = /^([+-]?)(nan|inf|infinity)$/i.exec(raw);
  if (!special) return undefined;
  if (special[2].toLowerCase() === 'nan') return Number.NaN;
  return special[1] === '-' ? -Infinity : Infinity;
}

/** Elevation from `ele` / `elevation` (a trailing unit "m" is tolerated). */
export function elevation(p: ImportProperties): number | undefined {
  const raw = value(p, 'ele', 'elevation');
  if (raw === undefined) return undefined;
  const cleaned = trimWhitespace(raw.toLowerCase().replaceAll('m', ''));
  const v = parseDouble(cleaned);
  return v !== undefined && Number.isFinite(v) ? v : undefined;
}

const sacScales: readonly SACScale[] = ['T1', 'T2', 'T3', 'T4', 'T5', 'T6'];

/** SAC grade from `sac_scale` (OSM values or T1…T6). */
export function sacScale(p: ImportProperties): SACScale | undefined {
  const raw = value(p, 'sac_scale', 'sacscale');
  if (raw === undefined) return undefined;
  const upper = raw.toUpperCase();
  if ((sacScales as readonly string[]).includes(upper)) return upper as SACScale;
  switch (raw.toLowerCase()) {
    case 'hiking':
      return 'T1';
    case 'mountain_hiking':
      return 'T2';
    case 'demanding_mountain_hiking':
      return 'T3';
    case 'alpine_hiking':
      return 'T4';
    case 'demanding_alpine_hiking':
      return 'T5';
    case 'difficult_alpine_hiking':
      return 'T6';
    default:
      return undefined;
  }
}

/** The Swift enum as a namespace object, for call sites that prefer `ImportPropertyMapping.poiCategory(…)`. */
export const ImportPropertyMapping = {
  token,
  value,
  isTruthy,
  categoryFromToken,
  poiCategory,
  poiAttributes,
  indicatesProtectedArea,
  protectedAreaKind,
  overnightRule,
  accessRule,
  seasonalWindow,
  regulationIDs,
  confidence,
  countryCode,
  name,
  elevation,
  sacScale,
} as const;
